import { IonContent, IonIcon, IonModal, IonSpinner, useIonToast } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
    closeCircle,
    closeOutline,
    downloadOutline,
    ellipsisHorizontal,
    logoInstagram,
    logoSnapchat,
    logoTiktok,
    pricetagsOutline,
    scanOutline,
    searchOutline,
    trashOutline,
    volumeHighOutline,
    volumeMuteOutline,
} from "ionicons/icons";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalytics } from "../../../AppAnalyticsProvider";
import { useInstagramShare } from "../../../hooks/useInstagramShare";
import useUserPins from "../../../hooks/useUserPins";
import StickerDesignsService from "../../../services/stickerDesigns.service";
import StickerTagsService from "../../../services/stickerTags.service";
import TrickFilterOptionsService from "../../../services/trickFilterOptions.service";
import { buildFlavorStickers, buildMomentStickers } from "../TrickStickerPicker/heroStickers";
import { imageUrlToDataUrl } from "../TrickStickerPicker/embedImage";
import { buildLostInTheSlushStickers } from "../TrickStickerPicker/lostInTheSlushTags";
import { buildPinStickers } from "../TrickStickerPicker/pinStickers";
import {
    buildCatalogStickers,
    catalogStickerDef,
    StickerDef,
    StickerSvg,
    ViewfinderSticker,
} from "../TrickStickerPicker/StickerDesigns";
import { buildTagTray, filterEligibleTags } from "../TrickStickerPicker/tagEligibility";
import { buildTrickStatSticker } from "../TrickStickerPicker/trickStatSticker";
import { persistPngForShare } from "../TrickStickerPicker/svgToPng";
import { compositeStickersWithMountedNodes, PlacedStickerWithNode } from "./composite";
import { sportToBackendKey, terrainToFeatureKey } from "./featureMap";
import { clampSheetDrag, SHEET_DRAG_ARM_THRESHOLD, shouldDismissSheet } from "./sheetDrag";
import {
    buildSheetRows,
    computeColumns,
    computeTileSize,
    estimateRowSize,
    SheetRow,
} from "./sheetRows";
import { useStickerHint } from "./useStickerHint";
import {
    buildBrandedClip,
    detectShareTargets,
    orderShareTargets,
    saveClipToGallery,
    ShareTargetId,
    ShareTargets,
    shareClipSheet,
    shareToSnapStory,
    shareToTikTokStory,
} from "./storyDestinations";
import "./StoryStudio.css";
import { isOverTrash, trashZoneCenter, TRASH_ZONE_RADIUS } from "./trashZone";
import { StickerTransform, useStickerGestures } from "./useStickerGestures";

interface StickerEditorProps {
    isOpen: boolean;
    onDismiss: () => void;
    tags: string[];
    sport: string;
    feature: string;
    thumbnail: string;
    username: string;
    badgeIds?: string[];
    // Optional post-share notify hook (e.g. UploadSuccess analytics/close). The
    // editor handles the Instagram hand-off itself, so callers don't need this.
    onConfirmShare?: (stickerImageUrl: string) => Promise<void> | void;
    // Story Studio additions: the clip itself + the data the default
    // Trick-Stat sticker is built from.
    videoUrl?: string;
    trickName?: string;
    location?: string;
}

interface Placed {
    instanceId: string;
    stickerDef: StickerDef;
    transform: StickerTransform;
}

type Destination = ShareTargetId;

// Each destination is an equal-size chip in its app's brand color. Installed
// social apps lead; Save / More are neutral. (No oversized "primary" — they're
// all the same size.)
interface DestMeta {
    label: string;
    icon: string;
    bg: string;
    fg: string;
    border?: string;
}
const DEST_META: Record<ShareTargetId, DestMeta> = {
    instagram: {
        label: "Instagram",
        icon: logoInstagram,
        bg: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
        fg: "#fff",
    },
    tiktok: { label: "TikTok", icon: logoTiktok, bg: "#010101", fg: "#fff", border: "1px solid rgba(37,244,238,0.55)" },
    snapchat: { label: "Snap", icon: logoSnapchat, bg: "#FFFC00", fg: "#000" },
    save: { label: "Save", icon: downloadOutline, bg: "rgba(255,255,255,0.10)", fg: "#e8eaed", border: "1px solid rgba(255,255,255,0.12)" },
    more: { label: "More", icon: ellipsisHorizontal, bg: "rgba(255,255,255,0.10)", fg: "#e8eaed", border: "1px solid rgba(255,255,255,0.12)" },
};

const NATURAL_STICKER_SIZE = 180;
const VARIANTS_PER_TAG = 2;

const StickerEditor: React.FC<StickerEditorProps> = ({
    isOpen,
    onDismiss,
    tags,
    sport,
    feature,
    thumbnail,
    username,
    badgeIds,
    onConfirmShare,
    videoUrl,
    trickName,
    location,
}) => {
    const { trackEvent } = useAnalytics();
    const { checkInstagram } = useInstagramShare();
    const [presentToast] = useIonToast();
    const canvasRef = useRef<HTMLDivElement>(null);
    const viewfinderHostRef = useRef<HTMLDivElement>(null);
    const placedMountRefs = useRef<Record<string, HTMLDivElement | null>>({});
    // Cache the composite + burned 9:16 clip keyed by the sticker arrangement, so
    // tapping a second destination (e.g. Save → More) reuses the same encode
    // instead of re-burning the whole video (which looked like a stall).
    const brandedCacheRef = useRef<{ sig: string; composite: string | null; branded: string | null } | null>(null);

    const [placed, setPlaced] = useState<Placed[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    // ECLIPTIC viewfinder frame defaults OFF — the clip leads; riders opt in to
    // the branded frame via the scan toggle.
    const [showViewfinder, setShowViewfinder] = useState<boolean>(false);
    // Story Studio preview plays with sound by default (the spawning surface mutes
    // its own background clip while we're open). Local-only toggle — never touches
    // the global VolumeProvider, so it can't change the feed's mute state.
    const [previewMuted, setPreviewMuted] = useState<boolean>(false);
    const [busy, setBusy] = useState<Destination | null>(null);
    const [sheetOpen, setSheetOpen] = useState<boolean>(false);
    const [stickerQuery, setStickerQuery] = useState<string>("");
    // The grid (~140 SVG tiles, growing) mounts only after the sheet first opens,
    // so editor-open stays snappy; once mounted it stays put (no slide-down flash).
    // The rows themselves are virtualized, so only the visible ones ever render.
    const [heavyMounted, setHeavyMounted] = useState<boolean>(false);
    const [dragging, setDragging] = useState<boolean>(false);
    // Sheet drag-to-dismiss: live downward offset (px) + whether a drag is active
    // (suppresses the slide transition so the sheet tracks the finger).
    const [sheetDragY, setSheetDragY] = useState<number>(0);
    const [sheetDragging, setSheetDragging] = useState<boolean>(false);
    // Measured width of the scrollable sheet body — drives the virtualized grid's
    // column count + tile size.
    const [sheetBodyWidth, setSheetBodyWidth] = useState<number>(0);
    const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
    const [shareTargets, setShareTargets] = useState<ShareTargets>({ instagram: false, tiktok: false, snapchat: false });

    // First-run nudge: pulse the "Stickers" button instead of auto-opening the
    // sheet. Self-extinguishes after a couple of opens (persisted in Preferences).
    const { pulse: stickerHintPulse, dismiss: dismissStickerHint } = useStickerHint(isOpen);

    // ─── Eligibility + trays (unchanged logic) ───────────────────────────
    const featureKey = useMemo(() => terrainToFeatureKey(feature), [feature]);
    const sportKey = useMemo(() => sportToBackendKey(sport), [sport]);

    const { data: eligibleUniverse } = useQuery({
        queryKey: ["stickerPublishedTags", sportKey, featureKey],
        queryFn: async () => {
            const published = await StickerTagsService.getPublishedTags(sportKey, featureKey);
            if (published !== null) return published;
            if (sportKey && featureKey) {
                return TrickFilterOptionsService.getTagOptions(sportKey, featureKey);
            }
            return null;
        },
        enabled: isOpen,
        staleTime: 1000 * 60 * 60 * 6,
    });

    const eligibleTags = useMemo(
        () => filterEligibleTags(tags, eligibleUniverse),
        [tags, eligibleUniverse],
    );
    const tray: StickerDef[] = useMemo(
        () => buildTagTray(eligibleTags, VARIANTS_PER_TAG),
        [eligibleTags],
    );

    const { pins, isLoading: pinsLoading } = useUserPins(badgeIds, isOpen);
    const pinTray: StickerDef[] = useMemo(() => buildPinStickers(pins), [pins]);
    const hasPins = !!badgeIds && badgeIds.length > 0;

    // "General" prefers the illustrated catalog (curated in idx-pins, served from
    // blob storage). Until that catalog is seeded/deployed the endpoint returns
    // null/empty and we fall back to the code-generated set (moment + flavor
    // heroes + the full Lost-in-the-Slush tag set), so the section is never blank.
    const { data: catalogDesigns } = useQuery({
        queryKey: ["stickerGeneralCatalog"],
        queryFn: () => StickerDesignsService.getGeneral(),
        enabled: isOpen,
        staleTime: 1000 * 60 * 60 * 3,
    });
    const catalog: StickerDef[] = useMemo(
        () => buildCatalogStickers(catalogDesigns ?? []),
        [catalogDesigns],
    );
    const lostInSlush: StickerDef[] = useMemo(() => buildLostInTheSlushStickers(), []);
    const general: StickerDef[] = useMemo(() => {
        const codegen = [...buildMomentStickers(username), ...buildFlavorStickers(), ...lostInSlush];
        if (catalog.length === 0) return codegen;
        // Bespoke catalog designs lead; the code-generated set fills in any tag the
        // catalog doesn't cover yet, so General is never sparse while the pack is
        // still being illustrated.
        const have = new Set(catalog.map((c) => (c.label ?? "").trim().toLowerCase()));
        return [...catalog, ...codegen.filter((d) => !have.has((d.label ?? "").trim().toLowerCase()))];
    }, [catalog, username, lostInSlush]);
    const generalFiltered: StickerDef[] = useMemo(() => {
        const q = stickerQuery.trim().toLowerCase();
        if (!q) return general;
        return general.filter((d) => (d.label ?? "").toLowerCase().includes(q));
    }, [general, stickerQuery]);

    // The viral default sticker, built from the clip's own data.
    const trickStat: StickerDef = useMemo(
        () => buildTrickStatSticker({ trickName, sport, location }),
        [trickName, sport, location],
    );

    // Detect which story apps are installed → the destination bar shows only
    // those, with Save promoted to primary when none are present.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        detectShareTargets().then((t) => { if (!cancelled) setShareTargets(t); });
        return () => { cancelled = true; };
    }, [isOpen]);
    const dests = useMemo(() => orderShareTargets(shareTargets), [shareTargets]);

    // ─── Canvas measurement (unchanged — observe from the ref callback) ───
    const roRef = useRef<ResizeObserver | null>(null);
    const handleCanvasRef = useCallback((el: HTMLDivElement | null) => {
        canvasRef.current = el;
        if (roRef.current) {
            roRef.current.disconnect();
            roRef.current = null;
        }
        if (!el) return;
        const measure = () => {
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) setCanvasSize({ w: rect.width, h: rect.height });
        };
        measure();
        if (typeof ResizeObserver !== "undefined") {
            const ro = new ResizeObserver(() => measure());
            ro.observe(el);
            roRef.current = ro;
        }
    }, []);
    useEffect(() => () => roRef.current?.disconnect(), []);

    // ─── Selection + gesture refs (synchronous, beat the next pointerdown) ─
    const placedRef = useRef(placed);
    const selectedIdRef = useRef(selectedId);
    useEffect(() => { placedRef.current = placed; }, [placed]);
    useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

    const selectInstance = useCallback((id: string | null) => {
        selectedIdRef.current = id;
        setSelectedId(id);
    }, []);

    const getSelectedTransform = useCallback((): StickerTransform | null => {
        const found = placedRef.current.find(p => p.instanceId === selectedIdRef.current);
        return found ? { ...found.transform } : null;
    }, []);

    const updateSelectedTransform = useCallback((next: StickerTransform) => {
        const sel = selectedIdRef.current;
        placedRef.current = placedRef.current.map(p =>
            p.instanceId === sel ? { ...p, transform: next } : p);
        setPlaced(placedRef.current);
    }, []);

    const removeInstance = useCallback((id: string) => {
        placedRef.current = placedRef.current.filter(p => p.instanceId !== id);
        setPlaced(placedRef.current);
        delete placedMountRefs.current[id];
    }, []);

    // ─── Drag-to-delete ──────────────────────────────────────────────────
    const handleGestureStart = useCallback(() => {
        if (selectedIdRef.current) setDragging(true);
    }, []);

    const handleGestureEnd = useCallback(() => {
        setDragging(false);
        const sel = selectedIdRef.current;
        const found = placedRef.current.find(p => p.instanceId === sel);
        const rect = canvasRef.current?.getBoundingClientRect();
        if (found && rect && isOverTrash(found.transform, rect.width, rect.height)) {
            removeInstance(found.instanceId);
            selectInstance(null);
            trackEvent("sticker_drag_deleted", { sticker_id: found.stickerDef.id });
        }
    }, [removeInstance, selectInstance, trackEvent]);

    useStickerGestures({
        canvasRef,
        enabled: isOpen,
        // Canvas mounts lazily inside the IonModal — only attach gesture
        // listeners once it's actually measured, or they'd bind to nothing.
        ready: canvasSize.w > 0,
        getTransform: getSelectedTransform,
        onChange: updateSelectedTransform,
        onGestureStart: handleGestureStart,
        onGestureEnd: handleGestureEnd,
    });

    // Live highlight of the trash target while a sticker hovers it.
    const overTrash = useMemo(() => {
        if (!dragging || !selectedId) return false;
        const found = placed.find(p => p.instanceId === selectedId);
        return isOverTrash(found?.transform ?? null, canvasSize.w, canvasSize.h);
    }, [dragging, selectedId, placed, canvasSize]);

    // ─── Place ───────────────────────────────────────────────────────────
    const addSticker = useCallback((def: StickerDef, scale = 1) => {
        const instanceId = `${def.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const w = canvasSize.w || 360;
        const h = canvasSize.h || 640;
        // Stagger each new sticker so it lands offset — not stacked on the last
        // one or the lower-third Trick-Stat — and reads as draggable from the off.
        const n = placedRef.current.length;
        const step = (n % 6) - 2.5;
        const cx = w / 2 + step * 26;
        const cy = h * 0.42 + step * 22;
        const next: Placed = { instanceId, stickerDef: def, transform: { x: cx, y: cy, scale, rotation: 0 } };
        placedRef.current = [...placedRef.current, next];
        setPlaced(placedRef.current);
        selectInstance(instanceId);
        if (def.id.startsWith("pin:")) {
            trackEvent("sticker_pin_placed", { badge_id: def.id.slice("pin:".length) });
        }
        return instanceId;
    }, [canvasSize.w, canvasSize.h, selectInstance, trackEvent]);

    const handlePickFromSheet = async (def: StickerDef) => {
        setSheetOpen(false);
        // Catalog stickers are remote artwork — embed to a data: URL before
        // placing so the share composite (canvas.toDataURL) stays taint-free.
        if (def.artworkUrl && !def.artworkUrl.startsWith("data:")) {
            const dataUrl = await imageUrlToDataUrl(def.artworkUrl);
            if (!dataUrl) {
                toast("Couldn't load that sticker — try another.", "danger");
                return;
            }
            addSticker(catalogStickerDef(def.id, def.label ?? "", dataUrl));
            return;
        }
        addSticker(def);
    };

    // ─── Open / close lifecycle ──────────────────────────────────────────
    const seededRef = useRef(false);
    // Reset everything when the editor closes so the next open starts clean.
    useEffect(() => {
        if (isOpen) return;
        seededRef.current = false;
        placedRef.current = [];
        setPlaced([]);
        selectedIdRef.current = null;
        setSelectedId(null);
        setSheetOpen(false);
        setStickerQuery("");
        setHeavyMounted(false);
        setShowViewfinder(false);
        setDragging(false);
        setSheetDragY(0);
        setSheetDragging(false);
        placedMountRefs.current = {};
        brandedCacheRef.current = null;
        // Reset the measured size too, so on the NEXT open `ready` flips
        // false→true once the canvas re-mounts and re-measures — which is what
        // re-attaches the gesture listeners. Without this, a stale non-zero
        // canvasSize keeps `ready` true, the effect never re-runs, and drag
        // silently dies after the first open.
        setCanvasSize({ w: 0, h: 0 });
    }, [isOpen]);

    // Auto-apply the Trick-Stat default sticker once the canvas is measured.
    // It is a normal, removable placed sticker — auto-placed, never forced.
    useEffect(() => {
        if (!isOpen || seededRef.current || canvasSize.w <= 0 || canvasSize.h <= 0) return;
        seededRef.current = true;
        const instanceId = `${trickStat.id}-${Date.now()}`;
        const next: Placed = {
            instanceId,
            stickerDef: trickStat,
            transform: { x: canvasSize.w / 2, y: canvasSize.h * 0.66, scale: 1.05, rotation: 0 },
        };
        placedRef.current = [next];
        setPlaced([next]);
    }, [isOpen, canvasSize.w, canvasSize.h, trickStat]);

    // No auto-open: the rider lands on their clip + the seeded hero sticker, and
    // the "Stickers" button pulses (useStickerHint) as the nudge to dig in.

    // Mount the sticker grid once the sheet first opens; keep it mounted
    // afterwards so closing doesn't flash an empty sheet. DEFER the mount off the
    // open frame: the grid renders ~40-60 filtered-SVG tiles in one commit, and
    // doing that in the same frame that animates the slide-up made the first
    // sheet-open janky. requestIdleCallback on web; double-rAF fallback on iOS
    // WKWebView (no rIC there) — either way the tile storm lands after the open
    // paint, and the existing tile pop-in animation makes it read as intentional.
    useEffect(() => {
        if (!sheetOpen || heavyMounted) return;
        const w = window as unknown as {
            requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
            cancelIdleCallback?: (id: number) => void;
        };
        let idleId: number | undefined;
        let rafId: number | undefined;
        if (typeof w.requestIdleCallback === "function") {
            idleId = w.requestIdleCallback(() => setHeavyMounted(true), { timeout: 200 });
        } else {
            rafId = requestAnimationFrame(() => {
                rafId = requestAnimationFrame(() => setHeavyMounted(true));
            });
        }
        return () => {
            if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
            if (rafId !== undefined) cancelAnimationFrame(rafId);
        };
    }, [sheetOpen, heavyMounted]);

    // ─── Open the sheet (and retire the first-run hint) ──────────────────────
    const openSheet = useCallback(() => {
        setSheetOpen(true);
        dismissStickerHint();
    }, [dismissStickerHint]);

    // ─── Sheet drag-to-dismiss ───────────────────────────────────────────────
    // Armed on the grabber/header only (never the scroll body), so it can't
    // fight the grid's scroll. A press becomes a drag once it moves past the arm
    // threshold; release past a distance/velocity threshold dismisses, else it
    // snaps back. See ./sheetDrag for the pure decision logic.
    const dragStartYRef = useRef<number>(0);
    const dragLastYRef = useRef<number>(0);
    const dragLastTRef = useRef<number>(0);
    const dragVelRef = useRef<number>(0);
    const dragArmedRef = useRef<boolean>(false);
    const dragCapturedRef = useRef<boolean>(false);

    const onSheetHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        dragArmedRef.current = true;
        dragCapturedRef.current = false;
        dragStartYRef.current = e.clientY;
        dragLastYRef.current = e.clientY;
        dragLastTRef.current = e.timeStamp;
        dragVelRef.current = 0;
    }, []);

    const onSheetHandlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragArmedRef.current) return;
        const total = e.clientY - dragStartYRef.current;
        // Promote to a real drag (and capture the pointer) once moved enough.
        if (!dragCapturedRef.current) {
            if (Math.abs(total) < SHEET_DRAG_ARM_THRESHOLD) return;
            dragCapturedRef.current = true;
            setSheetDragging(true);
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        }
        const dt = e.timeStamp - dragLastTRef.current;
        if (dt > 0) dragVelRef.current = (e.clientY - dragLastYRef.current) / dt;
        dragLastYRef.current = e.clientY;
        dragLastTRef.current = e.timeStamp;
        setSheetDragY(clampSheetDrag(total));
    }, []);

    const onSheetHandlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragArmedRef.current) return;
        const wasDragging = dragCapturedRef.current;
        dragArmedRef.current = false;
        dragCapturedRef.current = false;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        if (!wasDragging) return; // a tap — let child onClick (e.g. the X) fire
        setSheetDragging(false);
        const dy = clampSheetDrag(e.clientY - dragStartYRef.current);
        if (shouldDismissSheet(dy, dragVelRef.current)) {
            setSheetOpen(false);
        }
        setSheetDragY(0);
    }, []);

    // ─── Virtualized sticker grid ────────────────────────────────────────────
    // The whole sheet body is ONE scroll surface, virtualized as a flat list of
    // mixed rows (headers, tile-grids, skeleton, empty states) — so only the
    // visible rows mount, no matter how large the General catalog grows.
    const sheetScrollRef = useRef<HTMLDivElement | null>(null);
    const sheetBodyRoRef = useRef<ResizeObserver | null>(null);
    const handleSheetScrollRef = useCallback((el: HTMLDivElement | null) => {
        sheetScrollRef.current = el;
        sheetBodyRoRef.current?.disconnect();
        sheetBodyRoRef.current = null;
        if (!el) return;
        const measure = () => {
            const w = el.clientWidth;
            if (w > 0) setSheetBodyWidth(w);
        };
        measure();
        if (typeof ResizeObserver !== "undefined") {
            const ro = new ResizeObserver(() => measure());
            ro.observe(el);
            sheetBodyRoRef.current = ro;
        }
    }, []);
    useEffect(() => () => sheetBodyRoRef.current?.disconnect(), []);

    const columns = useMemo(() => computeColumns(sheetBodyWidth), [sheetBodyWidth]);
    const tileSize = useMemo(() => computeTileSize(sheetBodyWidth, columns), [sheetBodyWidth, columns]);
    const sheetRows: SheetRow[] = useMemo(
        () => buildSheetRows({
            columns,
            hasPins,
            pinsLoading,
            pinTray,
            pinSkeletonCount: badgeIds?.length ?? 0,
            general: generalFiltered,
            tray,
            query: stickerQuery,
        }),
        [columns, hasPins, pinsLoading, pinTray, badgeIds, generalFiltered, tray, stickerQuery],
    );

    const estimateSize = useCallback((i: number) => estimateRowSize(sheetRows[i], tileSize), [sheetRows, tileSize]);
    const getRowKey = useCallback((i: number) => sheetRows[i]?.key ?? i, [sheetRows]);
    const rowVirtualizer = useVirtualizer({
        count: sheetRows.length,
        getScrollElement: () => sheetScrollRef.current,
        estimateSize,
        getItemKey: getRowKey,
        overscan: 4,
        paddingEnd: 28,
        enabled: heavyMounted,
    });

    // ─── Composite + destinations ────────────────────────────────────────
    const buildComposite = async (): Promise<string | null> => {
        let cw = canvasSize.w;
        let ch = canvasSize.h;
        if ((cw <= 0 || ch <= 0) && canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            cw = rect.width;
            ch = rect.height;
        }
        if (cw <= 0 || ch <= 0) {
            console.warn("Story Studio canvas not measured; aborting composite");
            return null;
        }
        const withNodes: PlacedStickerWithNode[] = placed
            .map(p => {
                const svg = placedMountRefs.current[p.instanceId]?.querySelector("svg") as SVGSVGElement | null;
                if (!svg) return null;
                return { id: p.instanceId, natural: NATURAL_STICKER_SIZE, transform: p.transform, mountedSvg: svg };
            })
            .filter((x): x is PlacedStickerWithNode => x !== null);
        const viewfinderSvg = showViewfinder
            ? (viewfinderHostRef.current?.querySelector("svg") as SVGSVGElement | null)
            : null;
        const dataUrl = await compositeStickersWithMountedNodes(withNodes, viewfinderSvg, {
            canvasWidthCss: cw,
            canvasHeightCss: ch,
            includeViewfinder: showViewfinder,
        });
        return persistPngForShare(dataUrl, `composite-${Date.now()}.png`);
    };

    // Signature of the current sticker arrangement — when it's unchanged we can
    // reuse the cached composite + burned clip across destination taps.
    const placedSignature = () =>
        JSON.stringify({
            v: showViewfinder,
            p: placedRef.current.map(p => [
                p.instanceId,
                Math.round(p.transform.x),
                Math.round(p.transform.y),
                Number(p.transform.scale.toFixed(3)),
                Number(p.transform.rotation.toFixed(3)),
            ]),
        });

    // Build (or reuse) the composite PNG + burned 9:16 clip for the current
    // arrangement. The burn is the expensive step, so this cache is what stops
    // Save → More from re-encoding the whole video.
    const getBrandedClip = async (): Promise<{ composite: string | null; branded: string | null }> => {
        const sig = placedSignature();
        const cached = brandedCacheRef.current;
        if (cached && cached.sig === sig) return { composite: cached.composite, branded: cached.branded };
        const composite = await buildComposite();
        const branded = composite ? await buildBrandedClip(videoUrl, composite) : null;
        brandedCacheRef.current = { sig, composite, branded };
        return { composite, branded };
    };

    const toast = (message: string, color: "success" | "danger" | "medium" = "medium") =>
        presentToast({ message, duration: 2400, position: "top", color });

    const shareCaption = () => `${trickName || "Check this"} // @${username} // Ecliptic`;

    const handleDestination = async (dest: Destination) => {
        if (busy) return;
        setBusy(dest);
        try {
            // Build (or reuse the cached) overlay PNG + burned 9:16 clip, so the
            // stickers ride along to EVERY destination without re-encoding the
            // whole video on each tap (Save → More reuses the same encode).
            const { composite, branded } = await getBrandedClip();
            const clip = branded ?? videoUrl;

            if (dest === "instagram") {
                // Instagram's direct `instagram-stories://` deep link REQUIRES a
                // registered Facebook App ID as source_application — without it IG
                // shows "doesn't support sharing to Stories". So we only use the
                // seamless direct path when REACT_APP_FACEBOOK_APP_ID is set;
                // otherwise we share the branded clip via the OS sheet (the rider
                // taps Instagram → Stories), which needs no FB app.
                const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
                // Direct IG-Stories path only when a FB App ID is set AND Instagram
                // is installed; otherwise fall back to the OS sheet.
                let igResult: "shared" | "not-installed" | "error" | "sheet" = "sheet";
                if (fbAppId) {
                    igResult = await checkInstagram({ backgroundVideoURL: clip, sourceApplication: fbAppId });
                }
                trackEvent("story_share", { destination: "instagram", stickers: placed.length, branded: !!branded, direct: !!fbAppId, result: igResult });

                if (igResult === "error") {
                    // The direct hand-off failed mid-flight — keep the editor open
                    // (don't silently dismiss) so the rider can retry or pick another app.
                    toast("Couldn't open Instagram. Try again.", "danger");
                    return;
                }
                if (igResult !== "shared") {
                    // No FB App ID, or Instagram isn't installed → OS share sheet.
                    const result = await shareClipSheet(clip, trickName || "My clip", shareCaption());
                    if (result === "unsupported") { toast("Sharing is available in the app.", "medium"); return; }
                    if (result === "error") { toast("Couldn't open the share sheet.", "danger"); return; }
                }
                // Shared (directly or via the sheet) → confirm + close.
                if (composite) await onConfirmShare?.(composite);
                onDismiss();
                return;
            }

            if (dest === "save") {
                const result = await saveClipToGallery(clip);
                trackEvent("story_share", { destination: "save", branded: !!branded, result });
                if (result === "saved") toast("Clip saved to your camera roll.", "success");
                else if (result === "unsupported") toast("Saving is available in the app.", "medium");
                else toast("Couldn't save the clip.", "danger");
            } else {
                // tiktok / snapchat → their direct-story seam (OS-sheet fallback
                // until the SDKs are wired); more → the OS share sheet.
                const caption = shareCaption();
                const result =
                    dest === "tiktok" ? await shareToTikTokStory(clip, trickName || "My clip", caption)
                        : dest === "snapchat" ? await shareToSnapStory(clip, trickName || "My clip", caption)
                            : await shareClipSheet(clip, trickName || "My clip", caption);
                trackEvent("story_share", { destination: dest, branded: !!branded, result });
                if (result === "unsupported") toast("Sharing is available in the app.", "medium");
                else if (result === "error") toast("Couldn't open the share sheet.", "danger");
            }
        } catch (e) {
            console.error("Story share failed", e);
            toast("Something went wrong sharing.", "danger");
        } finally {
            setBusy(null);
        }
    };

    // ─── Render ──────────────────────────────────────────────────────────
    const trash = trashZoneCenter(canvasSize.w, canvasSize.h);

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
            <IonContent className="story-content" color="theme" scrollY={false}>
                <div className="relative w-full h-full bg-black select-none overflow-hidden">
                    {/* Full-bleed clip canvas (9:16, centred) */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div
                            ref={handleCanvasRef}
                            className="relative w-full overflow-hidden bg-black"
                            style={{ aspectRatio: "9 / 16", maxHeight: "100%", touchAction: "none" }}
                            onClick={(e) => { if (e.target === e.currentTarget) selectInstance(null); }}
                        >
                            {videoUrl ? (
                                <video
                                    src={videoUrl}
                                    poster={thumbnail || undefined}
                                    autoPlay
                                    loop
                                    // Sound while the editor is open (the spawning clip page /
                                    // UploadSuccess mutes its own background for the duration, so
                                    // only this preview is heard) — unless the user silences it with
                                    // the in-editor speaker toggle. Re-mute the instant we're closed:
                                    // IonModal keeps its content mounted after dismiss, so an
                                    // always-unmuted <video> would bleed audio behind the page.
                                    muted={!isOpen || previewMuted}
                                    playsInline
                                    // contain → preserve the clip's aspect inside the 9:16
                                    // story frame (black bars for non-9:16 clips); stickers
                                    // can be dragged onto the black space.
                                    className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                                />
                            ) : thumbnail ? (
                                <img
                                    src={thumbnail}
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                                    draggable={false}
                                />
                            ) : null}

                            {showViewfinder && (
                                <div ref={viewfinderHostRef} className="absolute inset-0 pointer-events-none">
                                    <ViewfinderSticker username={username} />
                                </div>
                            )}

                            {placed.map(p => (
                                <PlacedStickerView
                                    key={p.instanceId}
                                    placed={p}
                                    naturalSize={NATURAL_STICKER_SIZE}
                                    onSelect={() => selectInstance(p.instanceId)}
                                    hostRef={(el) => { placedMountRefs.current[p.instanceId] = el; }}
                                />
                            ))}

                            {/* Drag-to-delete target */}
                            {dragging && selectedId && (
                                <div
                                    className="story-trash absolute z-30 rounded-full flex items-center justify-center pointer-events-none"
                                    style={{
                                        left: trash.x,
                                        top: trash.y,
                                        width: TRASH_ZONE_RADIUS * 2,
                                        height: TRASH_ZONE_RADIUS * 2,
                                        marginLeft: -TRASH_ZONE_RADIUS,
                                        marginTop: -TRASH_ZONE_RADIUS,
                                        transform: overTrash ? "scale(1.12)" : "scale(1)",
                                        background: overTrash ? "rgba(205,92,92,0.32)" : "rgba(0,0,0,0.45)",
                                        border: `2px solid ${overTrash ? "#CD5C5C" : "rgba(255,255,255,0.5)"}`,
                                        backdropFilter: "blur(4px)",
                                    }}
                                >
                                    <IonIcon
                                        icon={trashOutline}
                                        style={{ fontSize: 22, color: overTrash ? "#CD5C5C" : "#fff" }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Top bar */}
                    <div
                        className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4"
                        style={{ paddingTop: "calc(0.75rem + var(--ion-safe-area-top, 0px))" }}
                    >
                        <IconButton label="Close" icon={closeOutline} onClick={onDismiss} />
                        <div className="flex gap-2.5">
                            <IconButton
                                label="Stickers"
                                icon={pricetagsOutline}
                                onClick={openSheet}
                                pulse={stickerHintPulse && !sheetOpen}
                            />
                            <IconButton
                                label={showViewfinder ? "Hide ECLIPTIC frame" : "Show ECLIPTIC frame"}
                                icon={scanOutline}
                                active={showViewfinder}
                                onClick={() => setShowViewfinder(v => !v)}
                            />
                            {videoUrl && (
                                <IconButton
                                    label={previewMuted ? "Unmute clip" : "Mute clip"}
                                    icon={previewMuted ? volumeMuteOutline : volumeHighOutline}
                                    onClick={() => setPreviewMuted(m => !m)}
                                />
                            )}
                        </div>
                    </div>

                    {/* Destination bar — shows only the apps that are installed,
                        with Save promoted to primary when none are. */}
                    {!sheetOpen && (
                        <div
                            className="absolute left-0 right-0 bottom-0 z-30 px-3.5 pt-10"
                            style={{
                                paddingBottom: "calc(0.9rem + var(--ion-safe-area-bottom, 0px))",
                                background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.65) 35%, rgba(0,0,0,0.92))",
                            }}
                        >
                            <div className="flex justify-start gap-3 overflow-x-auto pb-1" style={{ scrollSnapType: "x proximity" }}>
                                {dests.map(({ id }) => (
                                    <DestChip
                                        key={id}
                                        meta={DEST_META[id]}
                                        busy={busy === id}
                                        disabled={!!busy}
                                        onClick={() => handleDestination(id)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Scrim behind the sheet */}
                    {sheetOpen && (
                        <div
                            className="story-scrim absolute inset-0 z-40"
                            style={{ background: "rgba(0,0,0,0.45)" }}
                            onClick={() => setSheetOpen(false)}
                        />
                    )}

                    {/* Sticker sheet */}
                    <div
                        className={`story-sheet absolute left-0 right-0 bottom-0 z-50 rounded-t-3xl bg-[#0c0d0f] border-t border-white/10 flex flex-col${sheetDragging ? " no-sheet-transition" : ""}`}
                        style={{
                            height: "84%",
                            transform: sheetOpen ? `translateY(${sheetDragY}px)` : "translateY(101%)",
                            boxShadow: "0 -20px 50px rgba(0,0,0,0.6)",
                        }}
                        role="dialog"
                        aria-label="Sticker picker"
                        aria-hidden={!sheetOpen}
                    >
                        {/* Drag region — grabber + title. Drag down to dismiss;
                            armed here only so it never fights the grid's scroll. */}
                        <div
                            className="shrink-0"
                            style={{ touchAction: "none" }}
                            onPointerDown={onSheetHandlePointerDown}
                            onPointerMove={onSheetHandlePointerMove}
                            onPointerUp={onSheetHandlePointerUp}
                            onPointerCancel={onSheetHandlePointerUp}
                        >
                            <div className="pt-3 pb-1.5 flex justify-center cursor-grab active:cursor-grabbing">
                                <div className="w-10 h-1.5 rounded-full bg-white/25" />
                            </div>
                            <div className="flex items-center justify-between px-4 pb-2">
                                <h3 className="text-white text-base font-bold">Stickers</h3>
                                <button
                                    type="button"
                                    aria-label="Close stickers"
                                    onClick={() => setSheetOpen(false)}
                                    className="flex items-center justify-center p-3 -m-3"
                                >
                                    <IonIcon icon={closeOutline} style={{ fontSize: 22, color: "#fff" }} />
                                </button>
                            </div>
                        </div>

                        {/* Search — narrows the long General set by tag text. */}
                        <div className="px-4 pb-2.5 shrink-0">
                            <div className="flex items-center gap-2 h-9 rounded-xl bg-white/[0.06] border border-white/10 px-3">
                                <IonIcon icon={searchOutline} style={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }} />
                                <input
                                    type="text"
                                    inputMode="search"
                                    value={stickerQuery}
                                    onChange={(e) => setStickerQuery(e.target.value)}
                                    placeholder="Search stickers"
                                    aria-label="Search stickers"
                                    className="flex-1 bg-transparent text-sm text-white placeholder-white/60 outline-none"
                                />
                                {stickerQuery && (
                                    <button
                                        type="button"
                                        aria-label="Clear search"
                                        onClick={() => setStickerQuery("")}
                                        className="flex items-center justify-center p-2 -m-2"
                                    >
                                        <IonIcon icon={closeCircle} style={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {heavyMounted && (
                            <div
                                ref={handleSheetScrollRef}
                                className="flex-1 overflow-y-auto overscroll-contain"
                                style={{ position: "relative" }}
                            >
                                <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: "100%", position: "relative" }}>
                                    {rowVirtualizer.getVirtualItems().map((vi) => {
                                        const row = sheetRows[vi.index];
                                        if (!row) return null;
                                        return (
                                            <div
                                                key={vi.key}
                                                data-index={vi.index}
                                                ref={rowVirtualizer.measureElement}
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    transform: `translateY(${vi.start}px)`,
                                                    WebkitTransform: `translate3d(0, ${vi.start}px, 0)`,
                                                }}
                                            >
                                                <SheetRowView row={row} columns={columns} onPick={handlePickFromSheet} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </IonContent>
        </IonModal>
    );
};

// ─── Small presentational helpers ────────────────────────────────────────

const IconButton: React.FC<{ label: string; icon: string; active?: boolean; pulse?: boolean; onClick: () => void }> = ({
    label, icon, active, pulse, onClick,
}) => (
    <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        onClick={onClick}
        className={`w-11 h-11 rounded-full flex items-center justify-center${pulse ? " sticker-hint-pulse" : ""}`}
        style={{
            background: "rgba(20,20,22,0.5)",
            backdropFilter: "blur(8px)",
            border: `1px solid ${active ? "#0affff" : "rgba(255,255,255,0.12)"}`,
            boxShadow: active ? "0 0 12px rgba(10,255,255,0.5)" : "none",
        }}
    >
        <IonIcon icon={icon} style={{ fontSize: 20, color: active ? "#0affff" : "#fff" }} />
    </button>
);

// One equal-size chip per destination, in its app's brand color.
const DestChip: React.FC<{ meta: DestMeta; busy: boolean; disabled: boolean; onClick: () => void }> = ({
    meta, busy, disabled, onClick,
}) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        aria-label={meta.label}
        className="flex-shrink-0 w-[84px] h-[60px] rounded-2xl flex flex-col items-center justify-center gap-1 text-[11px] font-semibold disabled:opacity-60"
        style={{ background: meta.bg, color: meta.fg, border: meta.border ?? "none", scrollSnapAlign: "start" }}
    >
        {busy ? (
            <IonSpinner name="crescent" style={{ width: 20, height: 20 }} />
        ) : (
            <>
                <IonIcon icon={meta.icon} style={{ fontSize: 22 }} />
                {meta.label}
            </>
        )}
    </button>
);

const TILE_SIZE = 60;

// One picker tile — memoized so unrelated re-renders (drag, search typing)
// don't re-run the SVG render for every tile.
const SheetTile: React.FC<{
    def: StickerDef;
    border: string;
    stagger: boolean;
    index: number;
    onPick: (def: StickerDef) => void;
}> = memo(({ def, border, stagger, index, onPick }) => (
    <button
        type="button"
        onClick={() => onPick(def)}
        aria-label={def.label ? `Add ${def.label} sticker` : "Add sticker"}
        className={`story-tile aspect-square rounded-2xl bg-black border ${border} p-1.5 overflow-hidden grid place-items-center transition-transform active:scale-95`}
        data-stagger={stagger ? "" : undefined}
        style={stagger ? { animationDelay: `${index * 26}ms` } : undefined}
    >
        <StickerSvg sticker={def} size={TILE_SIZE} />
    </button>
));

// Renders one virtualized sheet row by kind (header / tile-grid / pins skeleton
// / empty state). Spacing uses padding, never margins, so the virtualizer's
// measureElement reads accurate row heights.
const SheetRowView: React.FC<{ row: SheetRow; columns: number; onPick: (def: StickerDef) => void }> = ({ row, columns, onPick }) => {
    const gridStyle = { gridTemplateColumns: `repeat(${Math.max(1, columns)}, minmax(0, 1fr))` };
    if (row.kind === "header") {
        return (
            <div className="flex items-baseline gap-2 px-4 pt-4 pb-2">
                <h4 className={`text-[11px] font-bold uppercase tracking-[0.14em] ${row.color}`}>{row.title}</h4>
                {typeof row.count === "number" && row.count > 0 && (
                    <span className="text-[10px] font-semibold text-white/55">{row.count}</span>
                )}
            </div>
        );
    }
    if (row.kind === "empty") {
        return <p className="text-white/60 text-sm px-4 pb-2">{row.text}</p>;
    }
    if (row.kind === "pinsLoading") {
        return (
            <div className="grid gap-2.5 px-4 pb-2.5" style={gridStyle}>
                {Array.from({ length: row.count }).map((_, i) => (
                    <div key={i} className="aspect-square rounded-2xl bg-black border border-neon-pink/30 flex items-center justify-center">
                        <IonSpinner name="crescent" />
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div className="grid gap-2.5 px-4 pb-2.5" style={gridStyle}>
            {row.defs.map((def, i) => (
                <SheetTile
                    key={def.id}
                    def={def}
                    border={row.border}
                    stagger={row.stagger && i < 16}
                    index={i}
                    onPick={onPick}
                />
            ))}
        </div>
    );
};

interface PlacedStickerViewProps {
    placed: Placed;
    naturalSize: number;
    onSelect: () => void;
    hostRef: (el: HTMLDivElement | null) => void;
}

const PlacedStickerView: React.FC<PlacedStickerViewProps> = ({ placed, naturalSize, onSelect, hostRef }) => {
    const { transform } = placed;
    return (
        <div
            ref={hostRef}
            onPointerDown={onSelect}
            className="absolute pointer-events-auto"
            style={{
                left: transform.x,
                top: transform.y,
                width: naturalSize,
                height: naturalSize,
                marginLeft: -naturalSize / 2,
                marginTop: -naturalSize / 2,
                transform: `rotate(${transform.rotation}rad) scale(${transform.scale})`,
                transformOrigin: "center center",
                borderRadius: 8,
            }}
        >
            <StickerSvg sticker={placed.stickerDef} size={naturalSize} />
        </div>
    );
};

export default StickerEditor;
